import { useCallback } from 'react'
import type { PropertiesPanelSectionId } from '@/lib/panels/types/properties-panel-section-id'

interface UseToolActivationInput {
  activeItem: string
  setActiveItem: (item: string) => void
  onToolActivate?: (item: string) => void
  isNodeEditMode: boolean
  onExitNodeEditMode?: () => void
  resetShapeCreation: () => void
  openSelectToolSection: (id: PropertiesPanelSectionId) => void
  collapsePropertiesPanelAllSections: () => void
  revealPanel: () => void
  togglePanel: (onCollapse: () => void) => void
}

/** Tool switching from the rail: activating a tool, clicking a rail icon, and the expand/collapse button. */
export function useToolActivation({
  activeItem,
  setActiveItem,
  onToolActivate,
  isNodeEditMode,
  onExitNodeEditMode,
  resetShapeCreation,
  openSelectToolSection,
  collapsePropertiesPanelAllSections,
  revealPanel,
  togglePanel,
}: UseToolActivationInput) {
  const activateTool = useCallback(
    (item: string) => {
      onToolActivate?.(item)
      setActiveItem(item)
    },
    [onToolActivate, setActiveItem]
  )

  const handleToolRailClick = (id: string) => {
    // While in node-edit mode, clicking the Select button exits node-edit
    // and stays on the Select panel rather than switching tools.
    if (id === 'select' && isNodeEditMode) {
      onExitNodeEditMode?.()
      return
    }

    // Switching tools should exit shape creation mode and reset shapes tool state.
    if (id !== 'shape') {
      resetShapeCreation()
    }

    activateTool(id)
    // When clicking Select tool, expand object properties section
    if (id === 'select') {
      openSelectToolSection('object')
      window.dispatchEvent(new Event('select-tool-activated'))
    }

    revealPanel()
  }

  const handleTogglePanel = () => {
    togglePanel(() => {
      // "Collapse tools" is this build's Collapse All trigger: closes every
      // Properties Panel section and clears their open-state (never the
      // selection itself) — see `usePropertiesPanelSections().collapseAll`.
      collapsePropertiesPanelAllSections()

      // Collapsing the panel while shapes tool is active should reset to Select
      if (activeItem === 'shape') {
        resetShapeCreation()
        activateTool('select')
      }
    })
  }

  return { activateTool, handleToolRailClick, handleTogglePanel }
}
